'use client';

import { faCar, faHotel, faPlane, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, ModalClose, ModalContent, ModalDescription, ModalHeading } from '@pickleballinc/react-ui';
import parse from 'html-react-parser';
import { useRouter, useSearchParams } from 'next/navigation';
import { RefObject, useEffect, useRef, useState } from 'react';

import { DetailsCard } from '@/tournament/components/details/DetailsCard';
import { FAQAccordion } from '@/tournament/components/details/FAQAccordion';
import NoEventsModal from '@/tournament/components/details/NoEventsModal';
import { TravelInfoList } from '@/tournament/components/details/TravelInfoList';
import { VenuesList } from '@/tournament/components/details/VenuesList';
import { LodgingInfo, TourneyLocation } from '@/types/tournaments.types';
import { FAQData } from '@/types/types';

interface TournamentDetailsInfo {
	locations: TourneyLocation[];
	description: string;
	additionalInfo: string;
	healthInfo: string;
	weatherInfo: string;
	refundInfo: string;
	weatherAlert: boolean;
	lotteryInfo: string;
	prizeInfo: string;
	lodgingData: LodgingInfo[];
	faqData: FAQData[];
	tourneyId: string;
}

interface TournamentDetailsProps {
	data: TournamentDetailsInfo;
	slug?: string;
}

export const DetailsSection = ({ data }: TournamentDetailsProps) => {
	const {
		locations,
		weatherAlert,
		weatherInfo,
		description,
		additionalInfo,
		refundInfo,
		lotteryInfo,
		healthInfo,
		prizeInfo,
		lodgingData,
		faqData,
		tourneyId
	} = data;

	const [weatherAlertModal, setWeatherAlertModal] = useState(weatherAlert);
	const [showNoEventsMessage, setShowNoEventsMessage] = useState(false);

	const router = useRouter();
	const searchParams = useSearchParams();
	const noEventsMessage = searchParams?.get('nef');

	//nef is param that will be passed from pbrackets for showing message that there is no opening events for specific player
	useEffect(() => {
		if (noEventsMessage === '1') {
			setShowNoEventsMessage(true);
		}
	}, [noEventsMessage]);

	const contactURL = `https://contact.${process.env.NEXT_PUBLIC_FRONT_URL}/contacts/tournament/${tourneyId}`;

	const venuesRef = useRef<HTMLDivElement>(null);
	const faqRef = useRef<HTMLDivElement>(null);

	const handleScroll = (ref: RefObject<HTMLDivElement>) => {
		if (ref.current) {
			const elementPosition = ref.current.getBoundingClientRect().top;

			const offsetPosition = window.scrollY + elementPosition - 320;

			window.scrollTo({
				top: offsetPosition,
				behavior: 'smooth'
			});
		}
	};

	return (
		<>
			<div className="flex flex-col gap-6">
				<div className="my-6 flex flex-row flex-wrap gap-4">
					<Button
						onClick={() => {
							handleScroll(venuesRef);
						}}
						variant="link-gray"
						className="font-normal !text-gray-900 underline"
					>
						Venue Details
					</Button>
					{faqData && faqData.length > 0 && (
						<>
							<div className=" block w-px bg-gray-400" />
							<Button
								onClick={() => {
									handleScroll(faqRef);
								}}
								variant="link-gray"
								className="font-normal !text-gray-900 underline"
							>
								FAQs
							</Button>
						</>
					)}

					{/* <div className=" block w-px bg-gray-400" /> */}
					{/* <Button variant="link-gray" className="font-normal !text-gray-900 underline">
					Linked Tournaments
				</Button>
				<div className=" block w-px bg-gray-400" />
				<Button variant="link-gray" className="font-normal !text-gray-900 underline">
					Stats
				</Button> */}
				</div>
				{description && (
					<DetailsCard
						title={'Tournament Description'}
						content={
							<>
								{description.split(/\n/).map((line: any, index: number) => {
									return (
										<div key={index} className="mb-4 text-sm sm:text-base">
											{parse(line)}
										</div>
									);
								})}
							</>
						}
					/>
				)}
				{additionalInfo && (
					<DetailsCard
						title={'Additional Information'}
						content={
							<>
								{additionalInfo.split(/\n/).map((line: any, index: number) => {
									return (
										<div key={index} className="mb-4 text-sm sm:text-base">
											{parse(line)}
										</div>
									);
								})}
							</>
						}
					/>
				)}
				{refundInfo && (
					<DetailsCard
						title={'Refund Policy'}
						content={
							<>
								{refundInfo.split(/\n/).map((line: any, index: number) => {
									return (
										<div key={index} className="mb-4 text-sm sm:text-base">
											{parse(line)}
										</div>
									);
								})}
							</>
						}
					/>
				)}
				{prizeInfo && (
					<DetailsCard
						title={'Prize Money'}
						content={
							<>
								{prizeInfo.split(/\n/).map((line: any, index: number) => {
									return (
										<div key={index} className="mb-4 text-sm sm:text-base">
											{parse(line)}
										</div>
									);
								})}
							</>
						}
					/>
				)}
				{lotteryInfo && (
					<DetailsCard
						title={'Lottery'}
						content={
							<>
								{lotteryInfo.split(/\n/).map((line: any, index: number) => {
									return (
										<div key={index} className="mb-4 text-sm sm:text-base">
											{parse(line)}
										</div>
									);
								})}
							</>
						}
					/>
				)}
				<div ref={venuesRef}>
					<DetailsCard title="Venue Details" content={<VenuesList locations={locations} />} expandable={false} />
				</div>
				{lodgingData && lodgingData.length > 0 && (
					<DetailsCard
						title="Travel information"
						content={
							<div className="flex flex-col gap-[6px] sm:gap-10">
								<TravelInfoList
									title="Lodging"
									prefixIcon={<FontAwesomeIcon icon={faHotel} />}
									data={lodgingData
										.filter((x) => x.AmenityTypeID === 1)
										.map((l) => {
											return {
												title: l.Venue,
												streetAddr: l.StreetAddress !== 'n/a' ? l.StreetAddress : '',
												city: l.City !== 'n/a' ? l.City : '',
												state: l.StateTitle !== 'n/a' ? l.StateTitle : '',
												zip: +l.Zip || 0,
												url: l.URL,
												milesFromVenue: +l.MilesFromTourney || 0,
												details: l.Details
											};
										})}
								/>
								<TravelInfoList
									title="Car rental"
									prefixIcon={<FontAwesomeIcon icon={faCar} />}
									data={lodgingData
										.filter((x) => x.AmenityTypeID === 2)
										.map((l) => {
											return {
												title: l.Venue,
												streetAddr: l.StreetAddress !== 'n/a' ? l.StreetAddress : '',
												city: l.City !== 'n/a' ? l.City : '',
												state: l.StateTitle !== 'n/a' ? l.StateTitle : '',
												zip: +l.Zip || 0,
												url: l.URL,
												milesFromVenue: +l.MilesFromTourney || 0,
												details: l.Details
											};
										})}
								/>
								<TravelInfoList
									title="Airlines"
									prefixIcon={<FontAwesomeIcon icon={faPlane} />}
									data={lodgingData
										.filter((x) => x.AmenityTypeID === 3)
										.map((l) => {
											return {
												title: l.Venue,
												streetAddr: l.StreetAddress !== 'n/a' ? l.StreetAddress : '',
												city: l.City !== 'n/a' ? l.City : '',
												state: l.StateTitle !== 'n/a' ? l.StateTitle : '',
												zip: +l.Zip || 0,
												url: l.URL,
												milesFromVenue: +l.MilesFromTourney || 0,
												details: l.Details
											};
										})}
								/>
							</div>
						}
					/>
				)}
				{weatherInfo && (
					<DetailsCard
						title={'Weather'}
						content={
							<>
								{weatherInfo.split(/\n/).map((line: any, index: number) => {
									return (
										<div key={index} className="mb-4 text-sm sm:text-base">
											{parse(line)}
										</div>
									);
								})}
							</>
						}
					/>
				)}
				{healthInfo && (
					<DetailsCard
						title={'Health & Safety'}
						content={
							<>
								{healthInfo.split(/\n/).map((line: any, index: number) => {
									return (
										<div key={index} className="mb-4 text-sm sm:text-base">
											{parse(line)}
										</div>
									);
								})}
							</>
						}
					/>
				)}

				{faqData && faqData.length > 0 && (
					<div ref={faqRef}>
						<DetailsCard title="FAQ" content={<FAQAccordion data={faqData} />} expandable={false} />
					</div>
				)}
			</div>

			<Modal onOpenChange={() => setWeatherAlertModal(!weatherAlertModal)} open={weatherAlertModal} backdropClose={false}>
				<ModalContent
					className="flex flex-col justify-center"
					style={{
						width: '85vw'
					}}
				>
					<ModalHeading className="flex w-full items-center justify-between">
						<div className="flex-1 text-xl font-semibold capitalize leading-7 text-gray-900">Weather Alert</div>
						<ModalClose className="ml-auto">
							<FontAwesomeIcon icon={faTimes} size="xl" className="text-gray-500" />
						</ModalClose>
					</ModalHeading>
					<ModalDescription className="mt-4 text-base font-normal text-gray-600">
						{weatherInfo.split(/\n/).map((line: any, index: number) => {
							return (
								<div key={index} className="mb-4">
									{parse(line)}
								</div>
							);
						})}
					</ModalDescription>
				</ModalContent>
			</Modal>
			<NoEventsModal
				open={showNoEventsMessage}
				onClose={() => setShowNoEventsMessage(false)}
				onConfirm={() => router.push(contactURL)}
				title="We're sorry"
				message="This tournament has no opening in any events that meet your gender, age and/or skill."
				confirmButtonText="Contact tournament director"
			/>
		</>
	);
};
